import {
  BulkInvitationColumnSpec,
  BulkInvitationRowError,
  BulkInvitationRowValues,
  readText,
} from "legal-entities/invitations/bulk/bulk-invitation-column-spec";

/**
 * The address parts, in the order the row editor shows them. Any one of them satisfies the API:
 * `Address.FromStrings` refuses an address with every field empty, so a roster that knows only the
 * country is enough to create the firm's legal entity.
 */
export const TEAM_LEADER_ADDRESS_KEYS = ["address1", "city", "state", "postalCode", "country"] as const;

/**
 * A client's list of the firms it wants to keep working with: one team leader per firm.
 *
 * This is the list `070-3p-process.md` calls the one the whole roll-out runs from, and the one it
 * says usually takes longest to get - which is why it arrives as a sheet rather than as thirty
 * separate decisions.
 */
const teamLeaderRosterColumns: BulkInvitationColumnSpec = {
  id: "team-leaders",
  noun: "team leader",
  templateFileName: "attorney-hub-team-leaders",
  templateSheetName: "Team leaders",
  fields: [
    {
      key: "email",
      header: "Email address",
      kind: "email",
      required: true,
      hint: "Where the invitation is sent. One per firm.",
    },
    { key: "firstName", header: "First name", kind: "text", required: true },
    { key: "lastName", header: "Last name", kind: "text", required: true },
    {
      key: "title",
      header: "Title",
      kind: "text",
      required: true,
      hint: "How they are addressed at the firm, such as Managing Partner.",
    },
    {
      key: "entityName",
      header: "Firm name",
      kind: "text",
      required: true,
      hint: "Ignored for a firm already on Attorney Hub, which has a name of its own.",
    },
    { key: "address1", header: "Firm address", kind: "text", required: false },
    { key: "city", header: "City", kind: "text", required: false },
    { key: "state", header: "State or province", kind: "text", required: false },
    { key: "postalCode", header: "Postal code", kind: "text", required: false },
    {
      key: "country",
      header: "Country",
      kind: "text",
      required: false,
      hint: "Any one part of the address will do. All of them empty will not.",
    },
    {
      key: "message",
      header: "Message",
      kind: "text",
      required: false,
      hint: "Added to the letter this firm receives. Optional.",
    },
  ],
  validateRow: (row: BulkInvitationRowValues): readonly BulkInvitationRowError[] => {
    const hasAnyAddress = TEAM_LEADER_ADDRESS_KEYS.some((key) => readText(row, key).length > 0);
    if (hasAnyAddress) return [];

    // Marked on the first address field rather than on all five, so the row editor puts one
    // message under one input instead of repeating itself down the form.
    return [
      {
        key: "address1",
        message: "Some part of the firm's address is required - a country on its own is enough.",
      },
    ];
  },
};

export default teamLeaderRosterColumns;
